import { FC, MouseEvent } from 'react';
import { Link } from '@carbon/react';
import { EMPTY, type RelatedItem, relatedItemTitle } from './types';
import { recordDetailHref, recordReaderFields } from './record-kind-configs';
import { formatBytes } from 'utils/number';
import { VestingDocumentDetail } from './vesting-document-detail';
import styles from './related-items.module.scss';

// Inline detail for an expanded row. Record kinds read the shared descriptors in
// record-kind-configs (so reader and writer can't drift); other kinds read row fields directly.

interface DetailField {
  label: string;
  value: string;
  fullWidth?: boolean;
  linkHref?: string;
}

const field = (
  label: string,
  value?: string | number | null,
  options?: { fullWidth?: boolean; linkHref?: string }
): DetailField => {
  const hasValue =
    value !== null && value !== undefined && String(value).trim() !== '';
  return {
    label,
    value: hasValue ? String(value) : EMPTY,
    fullWidth: options?.fullWidth,
    linkHref: options?.linkHref,
  };
};

// Mirrors DETAIL_FIELDS.external_title_support in the product mock.
const TITLE_SUPPORT_DETAIL_FIELDS = [
  { key: 'source', label: 'Source' },
  { key: 'fullRunsheet', label: 'Full Runsheet' },
  { key: 'ownership', label: 'Ownership' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'aiExtracted', label: 'AI Read' },
];

const buildFields = (doc: RelatedItem): DetailField[] => {
  switch (doc.kind) {
    case 'note':
      return [
        field('Full note text', doc.body ?? relatedItemTitle(doc), {
          fullWidth: true,
        }),
      ];
    case 'external_link':
      return [
        field('External Link', doc.label?.trim() || doc.url, {
          linkHref: doc.url ?? undefined,
        }),
      ];
    case 'external_title_support': {
      const d = (doc.details ?? {}) as Record<string, string | undefined>;
      return [
        ...TITLE_SUPPORT_DETAIL_FIELDS.map((rf) => field(rf.label, d[rf.key])),
        field('Attachment', doc.fileName),
        field('Comments', doc.body, { fullWidth: true }),
      ];
    }
    case 'uploaded_file':
      // Name / type / size all come off the blob server-side; the notes live in
      // `body`. An upload carries no `details` snapshot.
      return [
        field('File Name', doc.fileName),
        field('File Type', doc.fileType),
        field(
          'Size',
          doc.byteSize !== null && doc.byteSize !== undefined
            ? formatBytes(doc.byteSize)
            : undefined
        ),
        field('Notes', doc.body, { fullWidth: true }),
      ];
    default: {
      // Record kinds render from the shared descriptors. The identity field links to the
      // record page (derived from target/kind); cross-references link via a stored href key.
      const d = (doc.details ?? {}) as Record<string, unknown>;
      const identityHref = recordDetailHref(doc);
      return recordReaderFields(doc.kind).map((rf) => {
        const href = rf.recordLink
          ? identityHref
          : rf.hrefKey
            ? (d[rf.hrefKey] as string | undefined)
            : undefined;
        return field(
          rf.label,
          d[rf.key] as string | number | null | undefined,
          {
            linkHref: href ?? undefined,
          }
        );
      });
    }
  }
};

// Record kinds (and uploaded files) may carry an optional external link on the
// row itself — surface it after the snapshot fields.
const withOptionalExternalLink = (
  doc: RelatedItem,
  fields: DetailField[]
): DetailField[] => {
  const url = doc.url?.trim();
  if (!url) return fields;
  return [
    ...fields,
    field('External Link', doc.label?.trim() || url, { linkHref: url }),
  ];
};

const renderValue = (f: DetailField) => {
  if (f.linkHref && f.value !== EMPTY) {
    return (
      <Link
        href={f.linkHref}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.detailLink}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        {f.value}
      </Link>
    );
  }
  return f.value;
};

interface Props {
  document: RelatedItem;
  /** Hides write actions on kinds whose detail offers any. */
  readOnly?: boolean;
  /** Lets a kind whose detail can mutate the row ask the section to re-read its list. */
  onChanged?: () => void;
}

export const RelatedItemDetail: FC<Props> = ({
  document: doc,
  readOnly = false,
  onChanged,
}) => {
  // A vesting document expands to its card — the citation, the deal it came through, the
  // matcher's evidence and the two actions — rather than to a label/value grid. It is the one
  // kind whose detail is a component, so the dispatch lives here and the knowledge lives there.
  if (doc.kind === 'vesting_document') {
    return (
      <VestingDocumentDetail
        document={doc}
        readOnly={readOnly}
        onChanged={onChanged}
      />
    );
  }

  const fields =
    doc.kind === 'note' || doc.kind === 'external_link'
      ? buildFields(doc)
      : withOptionalExternalLink(doc, buildFields(doc));

  const isSingleFullWidth = fields.length === 1 && fields[0].fullWidth === true;

  return (
    <div className={styles.detail}>
      <dl
        className={`${styles.detailGrid}${
          isSingleFullWidth ? ` ${styles.detailGridFullNote}` : ''
        }`}
      >
        {fields.map((f) => (
          <div
            key={f.label}
            className={`${styles.detailItem}${
              f.fullWidth ? ` ${styles.detailItemFull}` : ''
            }`}
          >
            <dt>{f.label}</dt>
            <dd>{renderValue(f)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
